import { useState, useEffect, useRef } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { verifyResetToken, resetPassword } from '../services/auth';
import LockIcon from '../assets/images/Lock.svg';

export default function ChangePasswordCard() {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordLengthError, setPasswordLengthError] = useState('');
  const [serverErrors, setServerErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [passwordChanged, setPasswordChanged] = useState(false);
  const [isVerifyingToken, setIsVerifyingToken] = useState(true);
  const [tokenVerified, setTokenVerified] = useState(false);
  const [tokenError, setTokenError] = useState('');
  const [verificationComplete, setVerificationComplete] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const hasVerifiedRef = useRef(false);

  // Get token from URL query params (from backend redirect)
  const token = searchParams.get('token');

  const validationSchema = Yup.object({
    password: Yup.string()
      .min(8, 'Password must be at least 8 characters')
      .required('Password is required'),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref('password'), null], 'Passwords do not match')
      .required('Confirm Password is required'),
  });

  const formik = useFormik({
    initialValues: {
      password: '',
      confirmPassword: '',
    },
    validationSchema,
    onSubmit: () => {},
    validateOnBlur: false,
    validateOnChange: false,
  });

  useEffect(() => {
    // Prevent duplicate verification calls (e.g., in React Strict Mode)
    if (hasVerifiedRef.current) return;
    hasVerifiedRef.current = true;
    
    let cancelled = false;
    
    // Verify token when component mounts
    const verifyToken = async () => {
      if (!token) {
        if (cancelled) return;
        setTokenError('Invalid or missing reset token. Please request a new password reset.');
        setIsVerifyingToken(false);
        setVerificationComplete(true);
        toast.error('Invalid or missing reset token. Please request a new password reset.');
        setTimeout(() => {
          if (!cancelled) {
            navigate('/signin');
          }
        }, 2000);
        return;
      }

      setIsVerifyingToken(true);
      try {
        const response = await verifyResetToken({ token });
        
        // Don't check cancelled here - we want to update state even if component unmounts
        // Only check cancelled for side effects like navigation
        
        // Ensure we have a response object
        if (!response || typeof response !== 'object') {
          if (!cancelled) {
            setTokenVerified(false);
            setVerificationComplete(true);
            const errorMsg = 'Invalid response from server. Please try again.';
            setTokenError(errorMsg);
            toast.error(errorMsg);
          }
          return;
        }
        
        // Check if response has success property and it's explicitly true
        const isSuccess = response && response.success === true;
        
        if (isSuccess) {
          // Success case - token is valid
          // Always set state - don't check cancelled for state updates
          setTokenVerified(true);
          setTokenError('');
          setVerificationComplete(true);
        } else {
          // Error case - token is invalid or response doesn't indicate success
          // Extract error message from response
          const errorMsg = response?.error || response?.message || 'Invalid token provided. Please request a new reset link.';
          
          // Always set state - don't check cancelled for state updates
          setTokenVerified(false);
          setTokenError(errorMsg);
          setVerificationComplete(true);
          
          // Only check cancelled for side effects (toast, navigation)
          if (!cancelled) {
            // Show toast
            toast.error(errorMsg);
            
            setTimeout(() => {
              if (!cancelled) {
                navigate('/signin');
              }
            }, 3000);
          }
        }
      } catch (error) {
        if (cancelled) {
          setIsVerifyingToken(false);
          return;
        }
        const errorMessage =
          error?.response?.data?.error ||
          error?.response?.data?.message ||
          error?.message ||
          'Failed to verify token. Please try again.';
        setTokenVerified(false);
        setVerificationComplete(true);
        setTokenError(errorMessage);
        toast.error(errorMessage);
        setTimeout(() => {
          if (!cancelled) {
            navigate('/signin');
          }
        }, 3000);
      } finally {
        setIsVerifyingToken(false);
      }
    };

    verifyToken();
    
    return () => {
      cancelled = true;
    };
  }, [token, navigate]);

  const checkPasswordMatch = (pass, confirmPass) => {
    if (confirmPass && pass && pass !== confirmPass) {
      setPasswordError('Passwords do not match');
    } else {
      setPasswordError('');
      // Clear Formik errors when passwords match
      if (pass && confirmPass && pass === confirmPass) {
        formik.setFieldError('password', undefined);
        formik.setFieldError('confirmPassword', undefined);
      }
    }
  };

  const handlePasswordChange = (e) => {
    const newPassword = e.target.value;
    setPassword(newPassword);
    formik.setFieldValue('password', newPassword);

    // Clear server errors when user starts typing
    if (serverErrors.password) {
      setServerErrors((prev) => ({ ...prev, password: '' }));
    }

    if (newPassword && newPassword.length < 8) {
      setPasswordLengthError('Password must be at least 8 characters long');
    } else {
      setPasswordLengthError('');
      // Clear Formik error when password length is valid
      if (newPassword.length >= 8) {
        formik.setFieldError('password', undefined);
      }
    }

    checkPasswordMatch(newPassword, confirmPassword);
  };

  const handleConfirmPasswordChange = (e) => {
    const newConfirmPassword = e.target.value;
    setConfirmPassword(newConfirmPassword);
    checkPasswordMatch(password, newConfirmPassword);
    formik.setFieldValue('confirmPassword', newConfirmPassword);
    
    // Clear server errors when user starts typing
    if (serverErrors.confirmPassword) {
      setServerErrors((prev) => ({ ...prev, confirmPassword: '' }));
    }
    
    // Clear Formik error when confirm password changes
    if (password && newConfirmPassword && password === newConfirmPassword) {
      formik.setFieldError('confirmPassword', undefined);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isSubmitting || !token) {
      return;
    }

    setIsSubmitting(true);
    setServerErrors({});

    formik.setValues({
      password,
      confirmPassword,
    });
    formik.setTouched({
      password: true,
      confirmPassword: true,
    });
    const errors = await formik.validateForm();
    if (Object.keys(errors).length > 0) {
      setIsSubmitting(false);
      return;
    }

    if (!password || !confirmPassword) {
      toast.error('Please fill in all required fields');
      setIsSubmitting(false);
      return;
    }

    if (password.length < 8) {
      toast.error('Password must be at least 8 characters long');
      setIsSubmitting(false);
      return;
    }

    if (passwordLengthError) {
      toast.error('Please fix the password length before submitting.');
      setIsSubmitting(false);
      return;
    }

    if (passwordError) {
      toast.error('Please fix the password mismatch before submitting.');
      setIsSubmitting(false);
      return;
    }

    if (password !== confirmPassword) {
      toast.error('Passwords do not match. Please try again.');
      setIsSubmitting(false);
      return;
    }

    try {
      const response = await resetPassword({
        token,
        password,
        confirm_password: confirmPassword,
      });

      if (response.success) {
        setPasswordChanged(true);
        toast.success(response.message || 'Password reset successfully!');
      } else {
        const errorMessage = response.error || 'Failed to reset password. Please try again.';
        const errors = response.errors || {};

        if (errors && typeof errors === 'object') {
          const mapped = {};
          const setIf = (key, value) => {
            if (value && value.length)
              mapped[key] = Array.isArray(value)
                ? value.join(' ')
                : String(value);
          };

          setIf('password', errors.password);
          setIf('confirmPassword', errors.confirm_password);

          if (mapped.password) formik.setFieldError('password', mapped.password);
          if (mapped.confirmPassword)
            formik.setFieldError('confirmPassword', mapped.confirmPassword);

          setServerErrors(mapped);
        } else {
          toast.error(errorMessage);
        }
        setIsSubmitting(false);
      }
    } catch (err) {
      const serverData = err?.response?.data;
      const rawErrors = serverData?.errors;

      if (rawErrors && typeof rawErrors === 'object') {
        const mapped = {};
        const setIf = (key, value) => {
          if (value && value.length)
            mapped[key] = Array.isArray(value)
              ? value.join(' ')
              : String(value);
        };

        setIf('password', rawErrors.password);
        setIf('confirmPassword', rawErrors.confirm_password);

        if (mapped.password) formik.setFieldError('password', mapped.password);
        if (mapped.confirmPassword)
          formik.setFieldError('confirmPassword', mapped.confirmPassword);

        setServerErrors(mapped);
        setIsSubmitting(false);
        return;
      }

      const errorMessage =
        serverData?.detail ||
        serverData?.message ||
        err?.message ||
        'Failed to reset password. Please try again.';
      toast.error(errorMessage);
      setIsSubmitting(false);
    }
  };

  if (passwordChanged) {
    return (
      <div
        className="bg-white rounded-2xl border border-gray-200 shadow-sm w-full max-w-[660px] p-5 md:p-[60px] overflow-hidden"
        style={{ position: 'relative', zIndex: 1 }}
      >
        <div className="flex flex-col items-center gap-6 w-full">
          {/* Lock Icon */}
          <div className="flex items-center justify-center">
            <img
              src={LockIcon}
              alt="Lock"
              className="w-32 h-32 md:w-40 md:h-40"
            />
          </div>
          
          {/* Heading */}
          <div className="flex flex-col items-center gap-3 text-center">
            <p className="text-[#2B3133] font-libreBaskerville text-2xl md:text-4xl leading-[1em]">
              Password Changed!
            </p>
            <p className="text-[#889696] font-poppins text-sm md:text-base font-normal max-w-md mt-4">
              Your password has been reset successfully
            </p>
          </div>
          
          {/* Back to Login Button */}
          <button
            type="button"
            onClick={() => navigate('/signin')}
            className="w-full h-14 rounded-full bg-[#119975] text-[#FFEBDB] font-poppins text-lg font-medium hover:opacity-95 transition-opacity mt-4"
          >
            Back to Sign In
          </button>
        </div>
      </div>
    );
  }

  // Show loading state while verifying token
  if (isVerifyingToken) {
    return (
      <div
        className="bg-white rounded-2xl border border-gray-200 shadow-sm w-full max-w-[660px] p-5 md:p-[60px] overflow-hidden"
        style={{ position: 'relative', zIndex: 1 }}
      >
        <div className="flex flex-col items-center gap-6 w-full">
          <div className="w-16 h-16 rounded-full bg-[#E7F6EF] flex items-center justify-center">
            <svg
              className="animate-spin h-8 w-8 text-[#119975]"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
          </div>
          <p className="text-[#2B3133] font-libreBaskerville text-2xl md:text-3xl leading-[1em] text-center">
            Verifying Reset Link
          </p>
          <p className="text-[#889696] font-poppins text-sm md:text-base font-normal text-center">
            Please wait while we verify your reset link...
          </p>
        </div>
      </div>
    );
  }

  // Show error state if token is invalid or missing (only after verification is complete)
  if (verificationComplete && (!token || !tokenVerified)) {
    // Always show an error message - use tokenError if available, otherwise show default
    const displayError = tokenError || 'This password reset link is invalid or has expired. Please request a new one.';
    
    return (
      <div
        className="bg-white rounded-2xl border border-gray-200 shadow-sm w-full max-w-[660px] p-5 md:p-[60px] overflow-hidden"
        style={{ position: 'relative', zIndex: 1 }}
      >
        <div className="flex flex-col items-center gap-6 w-full">
          <p className="text-[#2B3133] font-libreBaskerville text-2xl md:text-3xl leading-[1em] text-center">
            Invalid Reset Link
          </p>
          <p className="text-[#889696] font-poppins text-sm md:text-base font-normal text-center max-w-md">
            {displayError}
          </p>
          <button
            type="button"
            onClick={() => navigate('/forgot-password')}
            className="w-full h-12 rounded-full bg-[#119975] text-[#FFEBDB] font-poppins text-lg font-medium hover:opacity-95 transition-opacity mt-4"
          >
            Request New Reset Link
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="bg-white rounded-2xl border border-gray-200 shadow-sm w-full max-w-[660px] p-5 md:p-[60px] overflow-hidden"
      style={{ position: 'relative', zIndex: 1 }}
    >
      <div className="flex flex-col items-center gap-3 w-full">
        <p className="text-[#2B3133] font-libreBaskerville text-3xl md:text-[40px] leading-[1em] text-center">
          Set New Password
        </p>
        <p className="text-[#889696] font-poppins text-sm md:text-base font-normal text-center mt-[1px] md:mt-0">
          Password must be at least 8 characters long. Please enter your new password below
        </p>
      </div>

      <form onSubmit={handleSubmit} className="flex flex-col gap-6 mt-10">
        <div className="flex flex-col items-center gap-[30px] w-full">
          <div className="flex flex-col justify-center items-start gap-2 w-full md:w-[540.51px] h-[85px] relative">
            <label className="text-[#889696] font-poppins text-sm font-medium">
              New Password
            </label>
            <div className="mt-1 flex items-center w-full md:w-[540.51px] h-[60px] rounded-xl border border-[#CBD5E0] bg-[#F8F8F8]">
              <input
                type={showPassword ? 'text' : 'password'}
                placeholder="@#*%"
                value={password}
                onChange={handlePasswordChange}
                className="flex-1 bg-transparent outline-none px-5 font-poppins"
                required
              />
              <div className="w-px h-10 bg-[#CFD9E0]" />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="pl-3 pr-4 cursor-pointer hover:opacity-70 transition-opacity"
              >
                {showPassword ? (
                  <svg
                    width="25"
                    height="24"
                    viewBox="0 0 25 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="w-6 h-6"
                  >
                    <path
                      d="M12.5 4.5C7.5 4.5 3.73 7.61 2.5 12C3.73 16.39 7.5 19.5 12.5 19.5C17.5 19.5 21.27 16.39 22.5 12C21.27 7.61 17.5 4.5 12.5 4.5ZM12.5 17C9.74 17 7.5 14.76 7.5 12C7.5 9.24 9.74 7 12.5 7C15.26 7 17.5 9.24 17.5 12C17.5 14.76 15.26 17 12.5 17ZM12.5 9C10.84 9 9.5 10.34 9.5 12C9.5 13.66 10.84 15 12.5 15C14.16 15 15.5 13.66 15.5 12C15.5 10.34 14.16 9 12.5 9Z"
                      fill="#889696"
                    />
                    <path
                      d="M2 2L22 22"
                      stroke="#889696"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </svg>
                ) : (
                  <svg
                    width="25"
                    height="24"
                    viewBox="0 0 25 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="w-6 h-6"
                  >
                    <path
                      d="M10.2276 12C10.2276 12.6962 10.5041 13.3639 10.9964 13.8562C11.4887 14.3484 12.1564 14.625 12.8526 14.625C13.5487 14.625 14.2164 14.3484 14.7087 13.8562C15.201 13.3639 15.4776 12.6962 15.4776 12C15.4776 11.3038 15.201 10.6361 14.7087 10.1438C14.2164 9.65156 13.5487 9.375 12.8526 9.375C12.1564 9.375 11.4887 9.65156 10.9964 10.1438C10.5041 10.6361 10.2276 11.3038 10.2276 12ZM23.0291 11.3953C20.8072 6.71484 17.4486 4.35938 12.9463 4.35938C8.44161 4.35938 5.08537 6.71484 2.86349 11.3977C2.77437 11.5864 2.72815 11.7925 2.72815 12.0012C2.72815 12.2099 2.77437 12.416 2.86349 12.6047C5.08537 17.2852 8.44396 19.6406 12.9463 19.6406C17.451 19.6406 20.8072 17.2852 23.0291 12.6023C23.2096 12.2227 23.2096 11.782 23.0291 11.3953ZM12.8526 16.125C10.5744 16.125 8.72755 14.2781 8.72755 12C8.72755 9.72188 10.5744 7.875 12.8526 7.875C15.1307 7.875 16.9776 9.72188 16.9776 12C16.9776 14.2781 15.1307 16.125 12.8526 16.125Z"
                      fill="#889696"
                    />
                  </svg>
                )}
              </button>
            </div>
            {passwordLengthError && (
              <p className="absolute top-full left-0 text-red-500 text-sm font-poppins mt-1 z-10">
                {passwordLengthError}
              </p>
            )}
            {formik.touched.password && formik.errors.password && (
              <p className="absolute top-full left-0 text-red-500 text-sm font-poppins mt-1 z-10 whitespace-nowrap overflow-hidden text-ellipsis">
                {formik.errors.password}
              </p>
            )}
            {!formik.errors.password && serverErrors.password && (
              <p className="absolute top-full left-0 text-red-500 text-sm font-poppins mt-1 z-10 whitespace-nowrap overflow-hidden text-ellipsis">
                {serverErrors.password}
              </p>
            )}
          </div>

          <div className="flex flex-col justify-center items-start gap-2 w-full md:w-[540.51px] h-[85px] relative">
            <label className="text-[#889696] font-poppins text-sm font-medium">
              New Password Confirmation
            </label>
            <div className="mt-1 flex items-center w-full md:w-[540.51px] h-[60px] rounded-xl border border-[#CBD5E0] bg-[#F8F8F8]">
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                placeholder="@#*%"
                value={confirmPassword}
                onChange={handleConfirmPasswordChange}
                className={`flex-1 bg-transparent outline-none px-5 font-poppins ${passwordError ? 'border-red-500' : ''}`}
                required
              />
              <div className="w-px h-10 bg-[#CFD9E0]" />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="pl-3 pr-4 cursor-pointer hover:opacity-70 transition-opacity"
              >
                {showConfirmPassword ? (
                  <svg
                    width="25"
                    height="24"
                    viewBox="0 0 25 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="w-6 h-6"
                  >
                    <path
                      d="M12.5 4.5C7.5 4.5 3.73 7.61 2.5 12C3.73 16.39 7.5 19.5 12.5 19.5C17.5 19.5 21.27 16.39 22.5 12C21.27 7.61 17.5 4.5 12.5 4.5ZM12.5 17C9.74 17 7.5 14.76 7.5 12C7.5 9.24 9.74 7 12.5 7C15.26 7 17.5 9.24 17.5 12C17.5 14.76 15.26 17 12.5 17ZM12.5 9C10.84 9 9.5 10.34 9.5 12C9.5 13.66 10.84 15 12.5 15C14.16 15 15.5 13.66 15.5 12C15.5 10.34 14.16 9 12.5 9Z"
                      fill="#889696"
                    />
                    <path
                      d="M2 2L22 22"
                      stroke="#889696"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </svg>
                ) : (
                  <svg
                    width="25"
                    height="24"
                    viewBox="0 0 25 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="w-6 h-6"
                  >
                    <path
                      d="M10.2276 12C10.2276 12.6962 10.5041 13.3639 10.9964 13.8562C11.4887 14.3484 12.1564 14.625 12.8526 14.625C13.5487 14.625 14.2164 14.3484 14.7087 13.8562C15.201 13.3639 15.4776 12.6962 15.4776 12C15.4776 11.3038 15.201 10.6361 14.7087 10.1438C14.2164 9.65156 13.5487 9.375 12.8526 9.375C12.1564 9.375 11.4887 9.65156 10.9964 10.1438C10.5041 10.6361 10.2276 11.3038 10.2276 12ZM23.0291 11.3953C20.8072 6.71484 17.4486 4.35938 12.9463 4.35938C8.44161 4.35938 5.08537 6.71484 2.86349 11.3977C2.77437 11.5864 2.72815 11.7925 2.72815 12.0012C2.72815 12.2099 2.77437 12.416 2.86349 12.6047C5.08537 17.2852 8.44396 19.6406 12.9463 19.6406C17.451 19.6406 20.8072 17.2852 23.0291 12.6023C23.2096 12.2227 23.2096 11.782 23.0291 11.3953ZM12.8526 16.125C10.5744 16.125 8.72755 14.2781 8.72755 12C8.72755 9.72188 10.5744 7.875 12.8526 7.875C15.1307 7.875 16.9776 9.72188 16.9776 12C16.9776 14.2781 15.1307 16.125 12.8526 16.125Z"
                      fill="#889696"
                    />
                  </svg>
                )}
              </button>
            </div>
            {passwordError && (
              <p className="absolute top-full left-0 text-red-500 text-sm font-poppins mt-1 z-10">
                {passwordError}
              </p>
            )}
            {formik.touched.confirmPassword && formik.errors.confirmPassword && (
              <p className="absolute top-full left-0 text-red-500 text-sm font-poppins mt-1 z-10 whitespace-nowrap overflow-hidden text-ellipsis">
                {formik.errors.confirmPassword}
              </p>
            )}
            {!formik.errors.confirmPassword && serverErrors.confirmPassword && (
              <p className="absolute top-full left-0 text-red-500 text-sm font-poppins mt-1 z-10 whitespace-nowrap overflow-hidden text-ellipsis">
                {serverErrors.confirmPassword}
              </p>
            )}
          </div>
        </div>

        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full h-14 rounded-full bg-[#119975] text-[#FFEBDB] font-poppins text-lg font-medium mt-4 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
        >
          {isSubmitting ? 'Resetting Password...' : 'Reset Password'}
        </button>
      </form>
    </div>
  );
}

